import React, { useContext, useId, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import FormContext from "react-bootstrap/FormContext";
import { IMAGE_ACCEPT, IMAGE_HINT } from "../../utils/helper/imageUpload";

/**
 * An image field's picker and hint (#308), in place of the browser's own file input, which wrote
 * "Choose file / No file chosen" in the browser's language whatever the page's.
 *
 * The real input is still there, visually hidden and out of the tab order: a "Choose image" button
 * opens it, so the keyboard reaches a focusable control. The input keeps the Form.Group's
 * controlId, so the group's <Form.Label> still labels it (and opens it).
 *
 * @param {object} props
 * @param {string} props.name The file input's name.
 * @param {(input: HTMLInputElement) => (Promise<void> | void)} props.onPick Called with the input once
 *   a file is picked. Every caller's handler (rejectImagePick, useImageUpload, useAuctionForm) empties
 *   the input when it refuses or fails to store the file; awaiting it is what lets the name shown
 *   here drop that file again.
 * @param {string} [props.id] The input's id; defaults to the Form.Group's controlId.
 */
export const ImageFileInput = ({ name, onPick, id }) => {
  const { controlId } = useContext(FormContext);
  const fallbackId = useId();
  const inputId = id || controlId || fallbackId;
  const hintId = `${inputId}-hint`;
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");

  const handleChange = async (event) => {
    const input = event.target;
    await onPick(input);
    // Read after the pick is handled, so this names only a file the form kept - and, when a later
    // pick superseded this one, that later file.
    setFileName(input.files?.[0]?.name ?? "");
  };

  return (
    <>
      <div className="image-file-input">
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          name={name}
          accept={IMAGE_ACCEPT}
          className="visually-hidden"
          tabIndex={-1}
          aria-describedby={hintId}
          onChange={handleChange}
        />
        <Button
          type="button"
          variant="outline-secondary"
          aria-describedby={hintId}
          onClick={() => inputRef.current?.click()}
        >
          Choose image
        </Button>
        <span className="image-file-name">{fileName || "No file chosen"}</span>
      </div>
      <p id={hintId} className="mt-2 image-upload-mess">
        {IMAGE_HINT}
      </p>
    </>
  );
};
